import type { Feature, Polygon, MultiPolygon, Position } from "geojson";
import { polygon, multiPolygon } from "@turf/helpers";
import { polygonArea } from "../geometry/operations/area";
import type { GeometryResult } from "../geometry/types";
import type { ZoneData, ZoneId } from "./types";
import { ZoneType } from "./types";

/** Minimum 4 positions for a valid ring (3 distinct + closing vertex) */
const MIN_RING_LENGTH = 4;

export class Zone {
	readonly id: ZoneId;
	name: string;
	readonly type: ZoneType;
	private _geometry: Feature<Polygon | MultiPolygon>;
	visible: boolean;

	constructor(id: ZoneId, name: string, type: ZoneType, geometry: Feature<Polygon | MultiPolygon>, visible = true) {
		this.id = id;
		this.name = name;
		this.type = type;
		this._geometry = geometry;
		this.visible = visible;
	}

	get geometry(): Feature<Polygon | MultiPolygon> {
		return this._geometry;
	}

	get polygonCount(): number {
		const g = this._geometry.geometry;
		return g.type === "Polygon" ? 1 : g.coordinates.length;
	}

	get totalArea(): number {
		return polygonArea(this._geometry);
	}

	// ---------- Ring access ----------

	getOuterRing(polyIndex: number): Position[] {
		return this._getRings(polyIndex)[0];
	}

	getHoles(polyIndex: number): Position[][] {
		return this._getRings(polyIndex).slice(1);
	}

	// ---------- Polygon-level mutations ----------

	addPolygon(rings: Position[][]): GeometryResult<void> {
		if (rings.length === 0 || rings[0].length < MIN_RING_LENGTH) {
			return { ok: false, error: "Rings must have at least 4 positions" };
		}

		const g = this._geometry.geometry;
		if (g.type === "Polygon") {
			this._geometry = multiPolygon([g.coordinates, rings]);
		} else {
			this._geometry = multiPolygon([...g.coordinates, rings]);
		}
		return { ok: true, data: undefined };
	}

	removePolygon(polyIndex: number): GeometryResult<void> {
		const g = this._geometry.geometry;
		if (g.type === "Polygon") {
			return { ok: false, error: "Cannot remove last polygon" };
		}
		if (polyIndex < 0 || polyIndex >= g.coordinates.length) {
			return { ok: false, error: `Index ${polyIndex} out of range` };
		}
		const remaining = g.coordinates.filter((_, i) => i !== polyIndex);
		if (remaining.length === 0) {
			return { ok: false, error: "Cannot remove last polygon" };
		}
		if (remaining.length === 1) {
			this._geometry = polygon(remaining[0]);
		} else {
			this._geometry = multiPolygon(remaining);
		}
		return { ok: true, data: undefined };
	}

	// ---------- Hole-level mutations ----------

	addHole(polyIndex: number, ring: Position[]): GeometryResult<void> {
		if (ring.length < MIN_RING_LENGTH) {
			return { ok: false, error: "Hole ring must have at least 4 positions" };
		}

		const g = this._geometry.geometry;
		if (g.type === "Polygon") {
			if (polyIndex !== 0) {
				return { ok: false, error: `Index ${polyIndex} out of range` };
			}
			this._geometry = polygon([...g.coordinates, ring]);
		} else {
			if (polyIndex < 0 || polyIndex >= g.coordinates.length) {
				return { ok: false, error: `Index ${polyIndex} out of range` };
			}
			const newCoords = g.coordinates.map((polyCoords, i) => (i === polyIndex ? [...polyCoords, ring] : polyCoords));
			this._geometry = multiPolygon(newCoords);
		}
		return { ok: true, data: undefined };
	}

	removeHole(polyIndex: number, holeIndex: number): GeometryResult<void> {
		const rings = this._getRings(polyIndex);
		// holeIndex 0 = first hole = rings[1]
		const ringIndex = holeIndex + 1;
		if (ringIndex >= rings.length) {
			return {
				ok: false,
				error: `Hole index ${holeIndex} out of range (${rings.length - 1} holes)`,
			};
		}

		const newRings = rings.filter((_, i) => i !== ringIndex);
		const g = this._geometry.geometry;
		if (g.type === "Polygon") {
			this._geometry = polygon(newRings);
		} else {
			const newCoords = g.coordinates.map((polyCoords, i) => (i === polyIndex ? newRings : polyCoords));
			this._geometry = multiPolygon(newCoords);
		}
		return { ok: true, data: undefined };
	}

	// ---------- Snapshot ----------

	snapshot(): ZoneData {
		return {
			id: this.id,
			name: this.name,
			type: this.type,
			geometry: JSON.parse(JSON.stringify(this._geometry)),
			visible: this.visible,
		};
	}

	static fromSnapshot(data: ZoneData): Zone {
		const geom = JSON.parse(JSON.stringify(data.geometry));
		return new Zone(data.id, data.name, data.type, geom, data.visible);
	}

	// ---------- Private ----------

	private _getRings(polyIndex: number): Position[][] {
		const g = this._geometry.geometry;
		if (g.type === "Polygon") {
			if (polyIndex !== 0) {
				throw new RangeError(`Index ${polyIndex} out of range for Polygon`);
			}
			return g.coordinates;
		}
		if (polyIndex < 0 || polyIndex >= g.coordinates.length) {
			throw new RangeError(`Index ${polyIndex} out of range for MultiPolygon (${g.coordinates.length} polygons)`);
		}
		return g.coordinates[polyIndex];
	}
}
